import { ReactElement } from "react";
import { CloseCircleFilled } from "@ant-design/icons";
import "./Terms.scss";
import { toHumanReadableSize } from "../util/general";

const MAX_UPLOAD_SIZE = parseInt(
  process.env.REACT_APP_MAX_UPLOAD_SIZE || "104857600",
  10,
);

// Add an event listener for keydown events
document.addEventListener("keydown", function (event) {
  // Check if the pressed key is the escape key (keyCode 27)
  if (event.keyCode === 27) {
    closeT(); // Pass the event object to closeT
    event.preventDefault(); // Prevents default behavior of the escape key
  }
});

// Updated closeT function to accept either a MouseEvent or a KeyboardEvent
function closeT() {
  var t = document.getElementById("cond");
  if (t) {
    t.style.display = "none";
  }
}

export default function Terms(): ReactElement {
  return (
    <>
      <div className="terms" id="cond">
        <button id="exit" onClick={closeT}>
          <CloseCircleFilled style={{ display: "block" }} />
          Close
        </button>
        <h2>
          Terms of Use <span>(Press Esc to exit)</span>
        </h2>
        <div>
          <p>
            Welcome to Varist's Community. By accessing and using this Community
            Edition, you agree to abide by the following Terms of Use
            (&quot;Terms&quot;). Please read these Terms carefully before
            proceeding.
          </p>
        </div>
        <h3>1. Accuracy and Uptime of service</h3>
        <div>
          <p>
            We make every effort to provide accurate and current information in
            this Community edition. However, please be aware that we cannot
            guarantee the correct:
          </p>
          <ol type="A">
            <li>
              <p>
                <strong>Varist Community</strong>: While we strive to provide
                accurate and comprehensive virus analysis, users should be aware
                that no analysis method is foolproof. Varist does not guarantee
                that all potential threats will be detected or neutralized. It
                is recommended that users employ multiple security measures and
                not rely solely on our service for ensuring the safety of their
                files.
              </p>
            </li>
            <li>
              <p>
                <strong>Information Accuracy</strong>: While we strive to ensure
                the accuracy of the information provided in this Community
                Edition, we cannot guarantee that all content is free from
                errors, omissions, or outdated data. There may be technical
                inaccuracies or typographical errors. Users are advised to
                verify the information independently before making any decisions
                based on it.
              </p>
            </li>
            <li>
              <p>
                <strong>Uptime</strong>: We endeavour to provide consistent
                access and uptime to our service but cannot guarantee
                uninterrupted service due to factors beyond our control such as
                maintenance needs, system failures, or external disruptions.
              </p>
            </li>
            <li>
              <p>
                <strong>Limitations</strong>: Varist cannot guarantee
                resolutions based on the information presented in this Demo and
                its accompanying documents, even if the content is intended to
                address particular challenges or issues.
              </p>
            </li>
            <li>
              <p>
                <strong>Changes to Content</strong>: Varist reserves the right
                to update, modify, or remove content at any given time without
                prior notice. Users are encouraged to frequently check for any
                updates or changes.
              </p>
            </li>
          </ol>
          <p>
            Varist cannot assure the resolution of problems based on the
            information furnished in this Demo and its accompanying documents,
            even if the intent is to tackle issues.
          </p>
        </div>
        <h3>2. User Responsibilities and Submission guidelines</h3>
        <div>
          <p>
            As a user, you must restrain from upload content that infringes upon
            the intellectual property rights of others, including copyrights,
            trademarks, and patents. Additionally, please respect the
            intellectual property rights of Varist and fellow Community users.
            Please do not upload files that contain sensitive or confidential
            information, or any material that you do not wish to share with
            others. Varist shall not be held responsible for any content you
            upload, and you should exercise discretion and responsibility when
            sharing any information or files through our platform (further
            information in section 5).
          </p>
          <p>
            We expect all users to use this Community edition responsibly and
            ethically. Any actions that disrupt, harm, or compromise the
            functionality or security of the Community edition are strictly
            prohibited and may result in legal action.
          </p>
          <p>You may not:</p>
          <ul>
            <li>
              <p>
                Reverse engineer, decompile, disassemble or work around
                technical limitations in the Services, without proper
                authorization.
              </p>
            </li>
            <li>
              <p>
                Disable, tamper with, or otherwise attempt to circumvent any
                mechanism that limits your use of the Services.
              </p>
            </li>
            <li>
              <p>
                Efforts that are excessively persistent or repeated are likewise
                not allowed and could lead to the suspension of an account. We
                urge you to adhere to these directives to ensure a secure and
                safe environment for everyone.
              </p>
            </li>
            <li>
              <p>
                Use the Services for any purpose that is unlawful or prohibited
                by this Terms of Use.
              </p>
            </li>
            <li>
              <p>
                Unauthorized access or endeavours to alter, erase, or manipulate
                content or systems could result in legal consequences.
              </p>
            </li>
          </ul>
          <p>
            Users are encouraged to promptly report any encountered issues,
            glitches, or susceptibilities while utilizing the service. We
            endorse the responsible disclosure of security vulnerabilities.
          </p>
          <h4>Submission Guidelines</h4>
          <p>File Size Limit:</p>
          <ul>
            <li>
              <p>
                All submitted files must be no larger than{" "}
                {toHumanReadableSize(MAX_UPLOAD_SIZE)}. Files that exceed this
                size limit will not be accepted.
              </p>
            </li>
            <li>
              <p>
                If you need to upload larger files, please consider compressing
                them or breaking them into smaller segments before submission.
              </p>
            </li>
          </ul>
          <p>Submission Process:</p>
          <ol>
            <li>Navigate to the 'Upload' section of our platform.</li>
            <li>Click on 'Choose File' or 'Browse' to select your file.</li>
            <li>
              Ensure your file is within the size limit and of the accepted file
              type.
            </li>
            <li>Click 'Submit' to upload your file.</li>
          </ol>
        </div>
        <h3>3. Purpose of Analysis/Data Collection</h3>
        <div>
          <p>
            Files uploaded by users are collected and analysed for multiple
            purposes, including but not limited to:
          </p>
          <ol type="A">
            <li>
              <p>
                <strong>Indicator Analysis Development</strong>: One of the
                primary reasons we collect uploaded files is to develop a robust
                and accurate indicator analysis for files. By understanding the
                various data points and patterns within the uploaded files, we
                can refine our algorithms and models to better serve our users.
              </p>
            </li>
            <li>
              <p>
                <strong>Rating Improvement</strong>: The files you provide are
                invaluable in refining our rating system. With each upload, we
                can enhance the precision and accuracy of our ratings, ensuring
                that users receive the most relevant and actionable insights
                from our service.
              </p>
            </li>
            <li>
              <p>
                <strong>Bug Detection and Resolution</strong>: By accessing and
                analyzing the uploaded files, we can detect anomalies, glitches,
                or unexpected behaviours in our system. This proactive approach
                aids us in identifying and rectifying bugs, ensuring a smoother
                and more efficient user experience.
              </p>
            </li>
            <li>
              <p>
                <strong>Data Privacy and Protection</strong>: We understand the
                importance of your data's privacy. The files uploaded are solely
                for the purposes mentioned above and will not be shared, sold,
                or used for other reasons without explicit user consent. All
                files are stored securely, and we employ industry-leading
                measures to protect against unauthorized access or breaches.
              </p>
            </li>
            <li>
              <p>
                <strong>Continuous Improvement</strong>: Our commitment is to
                continuously improve our service. With each file upload, you're
                not just using our service, but actively contributing to its
                betterment. We thank you for your trust and assure you that your
                data is being used responsibly and with the utmost respect.
              </p>
            </li>
          </ol>
          <p>
            We urge users to abstain from uploading files that contain personal
            or sensitive information.
          </p>
        </div>
        <h3>4. Data Access and Retention</h3>
        <div>
          <p>
            Please be aware that Community edition Services are designed for
            evaluation purposes only and are not suitable for use in a
            Production Environment. In the event that the Community edition
            Services Terms end or access to the Community edition Services is
            terminated, all Community edition Service Data will be retained, and
            Varist is not obligated to return this data to you or your
            organization.
          </p>
        </div>
        <h3>5. Confidentiality of Information</h3>
        <div>
          <p>
            When you engage with this software Community edition, it's important
            to understand that files or data uploaded by users are not
            considered confidential and will become the property of Varist.
            Regardless of the nature of the files or data you submit, they will
            not be treated as confidential.
          </p>
          <p>
            By submitting information or materials in this Community edition,
            you grant Varist an unrestricted, irrevocable license. This license
            includes a wide range of rights, such as the right to use,
            reproduce, publish, modify, distribute, and create derivative works.
          </p>
          <p>
            Moreover, it's essential to recognize that when you upload content
            while using this software Community edition, you are granting
            specific rights to the service. These rights include the authority
            to display, distribute, and perform other necessary actions to
            effectively showcase your uploaded content within the Community
            Edition's environment. These permissions are crucial for the smooth
            operation of the Community edition and its associated services.
          </p>
          <p>
            We prioritize the security and privacy of your submissions. All
            files are stored securely, and we employ industry-leading measures
            to protect against unauthorized access or breaches.
          </p>
        </div>
        <h3>6. Global Availability</h3>
        <div>
          <p>
            Please note that the information in this Community edition may refer
            to products, programs, or services that are not available in your
            country. These references do not indicate future availability. To
            inquire about offerings in your region, we recommend contacting your
            local Varist business representative.
          </p>
        </div>
        <h3>7. Intellectual Property</h3>
        <div>
          <p>
            All content, trademarks, logos, materials, and File analysis output
            in this Community edition are the property of Varist or its
            licensors and are protected by intellectual property laws. Any
            unauthorized use, reproduction, distribution, or modification of
            these materials is strictly prohibited.
          </p>
        </div>
        <h3>8. Ownership of User-Uploaded Content</h3>
        <div>
          <p>
            Please be aware that any content submitted or posted by users within
            this Community edition, including comments, reviews, or other
            contributions, is subject to review and approval by Varist. Varist
            retains the right to remove, modify, or reject user-generated
            content that violates these terms or is considered inappropriate or
            unlawful.
          </p>
          <p>
            By uploading files or data to this Community edition, you
            acknowledge and agree that Varist retains ownership of such files
            and data. This ownership includes the right to use, reproduce,
            publish, modify, distribute, and create derivative works based on
            the uploaded files and data.
          </p>
        </div>
        <h3>9. Linking to Our Site</h3>
        <div>
          <p>
            If you intend to create links to this Community edition, kindly
            request written approval from Varist. Approved links must not alter
            the presentation of our content, misrepresent our relationship, or
            harm our reputation.
          </p>
        </div>
        <h3>10. Data Protection and Security</h3>
        <div>
          <p>
            At Varist, safeguarding user data and ensuring security are
            paramount. We strictly adhere to data protection practices governing
            how user data is handled, stored, and safeguarded.
          </p>
          <p>
            The license granted also enables Varist to utilize any ideas,
            concepts, or techniques you provide, following our privacy policies
            that govern the handling of personally identifiable information.
            While Varist takes extensive measures to protect user privacy, it's
            essential to understand that the inherent nature of data
            transmission and storage cannot guarantee absolute security
          </p>
          <p>
            For further information please visit our Privacy Policy,{" "}
            <a href="https://www.varist.com/privacy">www.varist.com/privacy</a>
          </p>
        </div>
        <h3>11. Changes to Terms</h3>
        <div>
          <p>
            Varist reserves the right to update or modify these Terms at any
            time without prior notice. If you continue to use the Community
            Edition after changes have been made, it indicates your acceptance
            of the revised Terms. We recommend reviewing the Terms periodically.
          </p>
        </div>
        <h3>12. Indemnification</h3>
        <div>
          <p>
            Users agree to indemnify and hold Varist and its affiliates harmless
            from any claims, losses, damages, liabilities, and expenses arising
            from their use of this Community edition or violation of these
            Terms.
          </p>
        </div>
        <h3>13. Dispute Resolution</h3>
        <div>
          <p>
            Any disputes arising from these Terms of Use or Community edition
            usage will be resolved through arbitration or mediation, as
            applicable. Alternatively, disputes will be subject to the exclusive
            jurisdiction of the courts in Iceland.
          </p>
        </div>
        <h3>14. Contact Information</h3>
        <div>
          <p>
            For inquiries regarding these Terms or Community edition usage, you
            can contact Varist at{" "}
            <a href="mailto: info@varist.com">Info@varist.com</a>.
          </p>
        </div>
        <h3>15. Effective Date</h3>
        <div>
          <p>
            These Terms are effective as of 1st of November 2023. By continuing
            to use the Community edition after this date, you indicate your
            acceptance of these Terms.
          </p>
        </div>
      </div>
    </>
  );
}
